/**
 * Avatar — **parmak** mertebesi.
 *
 * Fotoğraf yok; **baş harfler** kullanılır. Gerekçe: panel kullanıcıları
 * fotoğraf yüklemiyor ve boş bir gri daire listeyi ağırlaştırıyor. Baş harf
 * hem yer kaplamıyor hem de satırı taranabilir kılıyor — göz "MK" ile "AY"yi
 * isimden daha hızlı ayırıyor.
 *
 * Renk **isimden türetilir**, rastgele değil: aynı kişi her ekranda aynı rengi
 * alır. Rastgele renk her yenilemede değişir ve tanınırlığı bozar.
 *
 * Renkler durum renklerinden AYRI bir paletten gelir — bir avatarın yeşil
 * olması "başarılı" demek değildir (docs/13 bölüm 6: renk rol taşır).
 */

import { cn } from '../../lib/cn';

/** Avatar tonları — durum renkleriyle karışmasın diye ayrı, yumuşak tonlar. */
const TONLAR = [
  'bg-[var(--avatar-1)] text-[var(--avatar-1-ink)]',
  'bg-[var(--avatar-2)] text-[var(--avatar-2-ink)]',
  'bg-[var(--avatar-3)] text-[var(--avatar-3-ink)]',
  'bg-[var(--avatar-4)] text-[var(--avatar-4-ink)]',
  'bg-[var(--avatar-5)] text-[var(--avatar-5-ink)]',
] as const;

const BOYLAR = {
  sm: 'size-7 text-xs',
  md: 'size-9 text-sm',
  lg: 'size-11 text-base',
} as const;

/** Ad soyaddan en fazla iki baş harf. */
function basHarfler(ad: string): string {
  const parcalar = ad
    .trim()
    .split(/\s+/)
    .filter((p) => p.length > 0);

  if (parcalar.length === 0) return '?';
  if (parcalar.length === 1) return (parcalar[0] ?? '').slice(0, 2).toLocaleUpperCase('tr-TR');

  const ilk = parcalar[0]?.[0] ?? '';
  const son = parcalar[parcalar.length - 1]?.[0] ?? '';
  return `${ilk}${son}`.toLocaleUpperCase('tr-TR');
}

/** İsimden kararlı bir ton seçer — aynı kişi daima aynı renk. */
function tonSec(anahtar: string): string {
  let toplam = 0;
  for (let i = 0; i < anahtar.length; i += 1) toplam = (toplam + anahtar.charCodeAt(i)) % 997;
  return TONLAR[toplam % TONLAR.length] ?? TONLAR[0];
}

export interface AvatarProps {
  ad: string;
  /** Ton seçimi için kararlı anahtar (e-posta). Verilmezse ad kullanılır. */
  anahtar?: string;
  boy?: keyof typeof BOYLAR;
  className?: string;
}

export function Avatar({ ad, anahtar, boy = 'md', className }: AvatarProps) {
  return (
    <span
      // Baş harfler süstür; ekran okuyucu zaten yandaki ismi okuyor.
      aria-hidden="true"
      title={ad}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-semibold select-none',
        BOYLAR[boy],
        tonSec(anahtar ?? ad),
        className
      )}
    >
      {basHarfler(ad)}
    </span>
  );
}
